import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoaderCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { DesignerApp } from '@/designer/DesignerApp';
import { applyProductMetrics, areaOf, designMeta, fromElement, signature, toDesign } from '@/designer/model';
import { AdminCustomerPreview } from './AdminCustomerPreview';

// Customer field slots: the box customers may fill. Text fields get the full area width so longer names still fit.
export const CATEGORIES = ['Familie', 'Hochzeit', 'Geburt', 'Haustiere', 'Natur', 'Sprüche', 'Portrait', 'Monogramm', 'Hobby', 'Geburtstag', 'Jahrestag', 'Firma'];

const withSlots = (design, product) => {
  const a = product.area;
  return { ...design, elements: design.elements.map(e => {
    if (!e.template_field) return e;
    if (e.template_slot) return e;
    if (e.kind !== 'text') return { ...e, template_slot: { x: e.x, y: e.y, w: e.w, h: e.h } };
    const pad = e.h * .25, y = Math.max(a.y, e.y - pad), bottom = Math.min(a.y + a.h, e.y + e.h + pad);
    return { ...e, template_slot: { x: a.x, y, w: a.w, h: Math.max(e.h, bottom - y) } };
  }) };
};

// The customer designer in template mode: same editor, but persistence goes to the article template (draft + publish).
export const AdminTemplateStudio = ({ product, identity }) => {
  const navigate = useNavigate();
  const [item, setItem] = useState(null), [loading, setLoading] = useState(identity !== 'neu'), [error, setError] = useState('');
  const [name, setName] = useState('Neue Vorlage'), [category, setCategory] = useState(''), [allowFree, setAllowFree] = useState(true), [session, setSession] = useState(0), [customer, setCustomer] = useState(null);
  const saved = useRef(null), itemRef = useRef(null); itemRef.current = item;
  const back = `/verwaltung/artikel/${product.id}?tab=templates`;
  useEffect(() => { document.title = 'Vorlagen-Studio · ManuCreator'; document.body.classList.add('designer-active'); return () => document.body.classList.remove('designer-active'); }, []);
  useEffect(() => { applyProductMetrics(product); }, [product]);
  const initial = useMemo(() => {
    if (!item) return { objects: [], meta: { allow_free_edit: true } };
    applyProductMetrics(product);
    return { objects: (item.design.elements || []).map(fromElement), meta: designMeta(item.design) };
  }, [item, product]);
  useEffect(() => {
    if (identity === 'neu') return;
    let alive = true;
    adminApi(`/article-templates/${identity}`).then(data => {
      if (!alive) return;
      setItem(data); setName(data.name); setCategory(data.category || ''); setAllowFree(data.allow_free_edit);
      applyProductMetrics(product);
      saved.current = { key: signature(toDesign((data.design.elements || []).map(fromElement), product, designMeta(data.design))), name: data.name, category: data.category || '', allowFree: data.allow_free_edit };
      setSession(s => s + 1);
    }).catch(e => { if (alive) setError(e.message); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [identity]); // eslint-disable-line react-hooks/exhaustive-deps
  const design = (objects, meta) => withSlots(toDesign(objects, product, { ...meta, allow_free_edit: allowFree, template_id: null, template_version: null, template_revision_id: null }), product);
  const save = async (objects, meta) => {
    const current = itemRef.current, body = { name: name.trim(), category, design: design(objects, meta), allow_free_edit: allowFree, sort_order: current?.sort_order || 0, revision: current?.revision || 1 };
    const result = await adminApi(`/article-templates${current ? '/' + current.id : ''}`, { method: current ? 'PUT' : 'POST', body });
    setItem(result); itemRef.current = result;
    saved.current = { key: signature(toDesign(objects, product, meta)), name: body.name, category, allowFree };
    if (!current) navigate(`/verwaltung/artikel/${product.id}/vorlagen/${result.id}`, { replace: true });
    return result;
  };
  const onPersist = {
    isSaved: (objects, meta) => { try { return !!saved.current && saved.current.key === signature(toDesign(objects, product, meta)) && saved.current.name === name.trim() && saved.current.category === category && saved.current.allowFree === allowFree; } catch { return false; } },
    run: async (target, objects, meta) => {
      const result = await save(objects, meta);
      if (target === 'publish') { const published = await adminApi(`/article-templates/${result.id}/publish`, { method: 'POST', body: { revision: result.revision } }); setItem(published); itemRef.current = published; return published; }
      return result;
    },
  };
  if (loading) return <main className="studio-loading"><LoaderCircle className="loading-spin" size={24} /><p data-testid="admin-template-loading">Vorlage wird geladen …</p></main>;
  if (error) return <main className="studio-loading"><p role="alert" className="studio-error" data-testid="admin-template-error">{error}</p></main>;
  const admin = { name, setName, category, setCategory, categories: CATEGORIES, allowFree, setAllowFree, published: !!item?.published_revision_id, back, openCustomerPreview: (objects, meta) => setCustomer(design(objects, meta)) };
  return <>
    <DesignerApp key={`${product.id}-${session}`} product={product} products={[product]} initial={initial} onProduct={() => toast('Vorlagen gehören zu genau einem Artikel.')} onPersist={onPersist} cartCount={0} admin={admin} />
    {customer && <AdminCustomerPreview design={customer} product={product} onClose={() => setCustomer(null)} />}
  </>;
};
